import { findRow, formatPrice } from "@/data/pricing";
import { specialisationFaqs } from "@/data/specialisationFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { fitCallUrl } from "@/lib/booking";
import { m } from "framer-motion";
import { ArrowRight, MonitorCog } from "lucide-react";
import {
  canonicalLinks,
  ogUrl,
  jsonLd,
  serviceSchema,
  breadcrumbSchema,
  faqPageSchema,
  webPageSchema, absoluteUrl, ogImage } from "@/lib/seo";
import { PhotoBand } from "@/components/site/PhotoBand";
import workshopFloorImg from "@/assets/photos/workshop-floor.webp";
import { NextStep } from "@/components/site/NextStep";
import { DeliveredThrough } from "@/components/site/CrossLinks";

const answerPrice = (name: string) => formatPrice(findRow(name));

const PATH = "/specialisations/systems-selection-and-implementation";
const TITLE =
  "Business systems selection and implementation";
const DESCRIPTION =
  "Requirements-led system selection, vendor evaluation, implementation planning and the adoption rhythm that makes a new business system stick. No software sold";

const CAREER_NOTE =
  "These results come from Adam's operating career before Ajility, inside the businesses themselves.";

const proof = [
  {
    figure:
      "Selected and implemented ten business platforms across operating roles, spanning ERP-adjacent, job management, pricing, reporting and field systems",
    org: "Senior operating roles",
  },
];

const covers = [
  {
    title: "Requirements first",
    desc: "What the operation actually has to do, written down and agreed, before a single demonstration is booked.",
  },
  {
    title: "Vendor evaluation",
    desc: "A scored comparison against those requirements, using your own scenarios in the demonstration rather than the vendor's script.",
  },
  {
    title: "Total cost and exit",
    desc: "Licences, implementation, integration, internal time and what it would take to leave, assessed before the contract is signed.",
  },
  {
    title: "Implementation planning",
    desc: "Sequence, data migration, cutover, testing and a named internal owner for each, with dates that survive contact with the business.",
  },
  {
    title: "Adoption",
    desc: "Training aimed at the way the work is actually done, and the removal of the old workaround, because both systems running is how adoption dies.",
  },
  {
    title: "The operating rhythm",
    desc: "The weekly and monthly cadence that uses the system's reporting, so the system becomes how the business is run rather than another place to enter data.",
  },
];

const faqs = specialisationFaqs["systems-selection-and-implementation"];

export const Route = createFileRoute("/specialisations/systems-selection-and-implementation")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("specialisations-systems-selection-and-implementation", `${TITLE} by Ajility`),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(webPageSchema({ mainEntityId: `${absoluteUrl(PATH)}#service`, path: PATH, name: `${TITLE} | Ajility`, description: DESCRIPTION })),
      jsonLd(
        breadcrumbSchema([
          { name: "Expertise", path: "/specialisations" },
          { name: "Systems selection and implementation", path: PATH },
        ]),
      ),
      jsonLd(
        serviceSchema({
          name: "Business systems selection and implementation",
          description: DESCRIPTION,
          path: PATH,
          slug: "systems-selection-and-implementation",
          serviceType: "Business systems selection and implementation",
        }),
      ),
      jsonLd(faqPageSchema(PATH, faqs, `${absoluteUrl(PATH)}#service`)),
    ],
  }),
  component: SystemsSpecialisation,
});

function SystemsSpecialisation() {
  return (
    <>
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-24 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <m.div
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative mx-auto max-w-7xl px-5 sm:px-8"
        >
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] text-steel-on-dark">
            <MonitorCog className="h-6 w-6" aria-hidden="true" />
          </div>
          <span className="eyebrow mt-8 block on-dark">
            Expertise: systems selection and implementation
          </span>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl text-balance">
            The right system, chosen for your operation, not the one with the best salesperson.
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-frost/75 text-pretty">
            {`Ajility leads system rollouts as initiative leadership, ${answerPrice("Scoped per initiative")}, covering implementation planning, data migration, cutover and adoption. It is vendor-independent, sells no software and takes no commissions.`}{" "}
            Ajility is vendor-independent and sells no software. The work is to write the
            requirements before the demonstrations, evaluate vendors against them, plan the
            implementation properly and <Link to="/insights/$slug" params={{ slug: "transport-118-percent" }} className="underline decoration-white/40 underline-offset-4 hover:decoration-frost">install the operating rhythm that makes people use the
            system after go-live</Link>.
          </p>
          <div className="mt-10 flex flex-wrap items-start gap-4">
            <BookFitCall
              placement="specialisations_systems_selection_hero"
              buttonClassName="group inline-flex items-center gap-2 rounded-full bg-frost px-6 py-3 text-sm font-semibold text-midnight-deep transition hover:bg-white"
              noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
            >
              Book a fit call
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
            </BookFitCall>
            <Link
              to="/packages"
              className="inline-flex rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-frost/85 transition hover:border-white/30 hover:text-frost"
            >
              See the Systems & Operational Improvement install
            </Link>
          </div>
        </m.div>
      </section>

      <PhotoBand
        src={workshopFloorImg}
        alt="Staff working at benches on a light-industrial workshop floor"
        priority
      />

      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow">What the work covers</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            Six steps from requirement to routine use
          </h2>
          <p className="mt-4 max-w-3xl text-charcoal">
            A system is only worth what people do with it. The selection decides whether it can
            work; the implementation and the rhythm decide whether it does.
          </p>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {covers.map((c, i) => (
              <m.div
                key={c.title}
                initial={{ y: 16 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-panel"
              >
                <h3 className="font-serif text-xl text-foreground">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal">{c.desc}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-surface py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">Independence</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">
            No software is sold here, and no vendor pays us
          </h2>
          <p className="mt-6 leading-relaxed text-charcoal">
            Ajility holds no reseller agreements, no implementation partnerships and no referral
            arrangements, and takes no commission, rebate or margin from any vendor. The
            recommendation is written against your requirements, and the reasoning is shown so you
            can disagree with it.
          </p>
          <p className="mt-4 leading-relaxed text-charcoal">
            Sometimes the recommendation is to keep the system you already have and fix the way it
            is configured and used. That answer is cheaper than a replatform and is offered where it
            is the right one.
          </p>
        </div>
      </section>

      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow">Systems experience</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            Platform work that spans both sides of the decision
          </h2>
          <p className="mt-6 max-w-3xl leading-relaxed text-charcoal">
            Systems and process have run through every role since 2007. The first job in the
            record was training store teams on operating systems. Since then, the work has
            covered twenty-plus named platforms across five categories: ERP (SAP), project and
            job management (Procore, SIMPRO, Ignition), ATS/CRM (Bullhorn, Pipedrive), finance
            and reporting (Xero, Fathom), and workforce and work management (Deputy, Time2u,
            ClickUp, Asana, Microsoft Planner, PandaDoc).
          </p>
          <p className="mt-4 max-w-3xl leading-relaxed text-charcoal">
            That experience spans both sides of the decision: writing business requirements and
            negotiating with vendors, including renegotiating a major platform renewal at 70%
            below the original quote, and the unglamorous half, meaning implementation, data cleanup,
            training the people who use it, and the adoption rhythm afterwards.
          </p>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="card-panel">
              <h3 className="font-serif text-xl text-foreground">ERP</h3>
              <p className="mt-3 text-sm leading-relaxed text-charcoal">SAP</p>
            </div>
            <div className="card-panel">
              <h3 className="font-serif text-xl text-foreground">Project and job management</h3>
              <p className="mt-3 text-sm leading-relaxed text-charcoal">Procore, SIMPRO, Ignition</p>
            </div>
            <div className="card-panel">
              <h3 className="font-serif text-xl text-foreground">ATS/CRM</h3>
              <p className="mt-3 text-sm leading-relaxed text-charcoal">Bullhorn, Pipedrive</p>
            </div>
            <div className="card-panel">
              <h3 className="font-serif text-xl text-foreground">Finance and reporting</h3>
              <p className="mt-3 text-sm leading-relaxed text-charcoal">Xero, Fathom</p>
            </div>
            <div className="card-panel md:col-span-2 lg:col-span-1">
              <h3 className="font-serif text-xl text-foreground">Workforce and work management</h3>
              <p className="mt-3 text-sm leading-relaxed text-charcoal">Deputy, Time2u, ClickUp, Asana, Microsoft Planner, PandaDoc</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-midnight py-24 text-frost">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow on-dark">Track record</span>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-balance">
            Systems work delivered in senior operating roles
          </h2>
          <p className="mt-4 max-w-3xl text-steel-on-dark">{CAREER_NOTE}</p>
          <ul className="mt-12 grid gap-6 md:grid-cols-2">
            {proof.map((p) => (
              <li key={p.figure} className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
                <p className="font-serif text-lg leading-snug text-frost">{p.figure}</p>
                <p className="mt-3 text-sm text-steel-on-dark">{p.org}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-mist py-20">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <span className="eyebrow">Where this is bought</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground">
            Systems & Operational Improvement install
          </h2>
          <p className="mt-4 text-charcoal">
            Selection and implementation are delivered through the Systems & Operational
            Improvement service line, or scoped first through the Operating Diagnostic where it is
            not yet clear that a system is the constraint. Both are published, with their scope and
            fee, on the packages page.
          </p>
          <p className="mt-4 text-charcoal">Read <Link to="/insights/$slug" params={{ slug: "ai-adoption-small-business" }} className="underline underline-offset-4">AI adoption in a small business: why the team won't use the tools you paid for</Link> for the practical questions behind this work.</p>
          <Link
            to="/packages"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-midnight px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-midnight-deep"
          >
            See the install and the Operating Diagnostic
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="bg-background py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">Common questions</h2>
          <dl className="mt-10 space-y-6">
            {faqs.map((f) => (
              <div key={f.q} className="card-panel">
                <dt>
                  <h3 className="font-serif text-lg text-foreground">{f.q}</h3>
                </dt>
                <dd className="mt-3 text-sm leading-relaxed text-charcoal">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <DeliveredThrough slug="systems-selection-and-implementation" />

      <NextStep
        heading="Start with the free Operating Scan"
        body="21 plain statements about how the business actually runs, answered from Not us to Always. In about six minutes you see a provisional score naming the weakest areas, then choose whether you want a conversation."
        primaryLabel="Take the free Operating Scan"
        primaryTo="/scan"
        secondaryLabel="Book a fit call"
        secondaryTo={fitCallUrl("specialisations_systems_selection_footer")}
        secondaryNote={
          <Link to="/contact" className="text-sm font-medium text-frost/70 hover:text-frost">
            Or send a note instead
          </Link>
        }
      />
    </>
  );
}
